import dagre from "@dagrejs/dagre";
import {
  Background,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Badge from "@intility/bifrost-react/Badge";
import { useEffect } from "react";
import "./BlueprintCanvas.css";

interface TreeNode {
  label: string;
  kind: "company" | "platform" | "resource";
  typeKind?: "static" | "dynamic";
  children?: TreeNode[];
}

type NodeData = {
  label: string;
  kind: "company" | "platform" | "resource";
  typeKind?: "static" | "dynamic";
};

const NODE_WIDTH = 200;
const NODE_HEIGHT = 56;

function MiniNodeCard({ data }: NodeProps<Node<NodeData>>) {
  return (
    <div
      className={`bp-canvas-node bp-canvas-node-${data.kind}`}
      title={data.label}
    >
      <Handle type="target" position={Position.Top} isConnectable={false} />
      <div className="bp-canvas-node-content">
        <span className="bp-canvas-node-label">{data.label}</span>
        {data.typeKind && (
          <Badge state={data.typeKind === "static" ? "neutral" : "chill"} pill>
            {data.typeKind === "static" ? "Static" : "Dynamic"}
          </Badge>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} isConnectable={false} />
    </div>
  );
}

const nodeTypes = { card: MiniNodeCard };

function buildMiniGraph(root: TreeNode): {
  nodes: Node<NodeData>[];
  edges: Edge[];
} {
  const nodes: Node<NodeData>[] = [];
  const edges: Edge[] = [];

  function walk(tree: TreeNode, parentId: string | null) {
    const id = parentId ? `${parentId}-${tree.label}` : tree.label;
    nodes.push({
      id,
      type: "card",
      position: { x: 0, y: 0 },
      data: { label: tree.label, kind: tree.kind, typeKind: tree.typeKind },
    });
    if (parentId) {
      edges.push({ id: `e-${parentId}-${id}`, source: parentId, target: id });
    }
    for (const child of tree.children ?? []) {
      walk(child, id);
    }
  }

  walk(root, null);

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 60 });
  for (const n of nodes) {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const e of edges) {
    g.setEdge(e.source, e.target);
  }
  dagre.layout(g);

  for (const n of nodes) {
    const pos = g.node(n.id);
    n.position = { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 };
  }

  return { nodes, edges };
}

function MiniCanvasInner({ root }: { root: TreeNode }) {
  const { nodes: initialNodes, edges: initialEdges } = buildMiniGraph(root);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { fitView } = useReactFlow();

  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = buildMiniGraph(root);
    setNodes(newNodes);
    setEdges(newEdges);
    requestAnimationFrame(() => fitView({ padding: 0.2, duration: 200 }));
  }, [root, setNodes, setEdges, fitView]);

  return (
    <div className="bp-canvas-wrapper" style={{ height: 480 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={1.2}
      >
        <Background gap={20} />
      </ReactFlow>
    </div>
  );
}

export default function MiniCanvas({ root }: { root: TreeNode }) {
  return (
    <ReactFlowProvider>
      <MiniCanvasInner root={root} />
    </ReactFlowProvider>
  );
}

export type { TreeNode };
